import moment from 'moment'
import queryString from 'query-string'

import { history, store as reduxStore } from 'index'
import settings from 'popups/Settings'

import {
  getSearchParams,
  timeFormat,
  emailAddress,
  getSearchParamProps,
  checkPageReplace,
} from 'utils'
import { isEmail } from 'utils/ui'

/* inherit - default: true - 이전에 설정된 parameter를 그대로 유지하면서, 새로 지정된 parameter만 변경. */
export function setSearchParams(
  params: Record<string, any> | null | undefined,
  inherit?: boolean,
): void {
  if (params == null) {
    return
  }

  // let url = window.location.origin + window.location.pathname

  const prevParams = getSearchParams()

  let newParams: Record<string, any> = {
    ...params,
  }

  if (inherit !== false) {
    newParams = {
      ...prevParams,
      ...params,
    }
  }

  let isChanged = false
  const prevCount = Object.keys(prevParams).length
  const newCount = Object.keys(newParams).length
  if (prevCount !== newCount) {
    isChanged = true
  } else {
    Object.entries(newParams).forEach(([key, value]) => {
      if (isChanged === false && prevParams[key] !== params[key]) {
        isChanged = true
      }
      if (value === undefined) {
        delete newParams[key]
      }
    })
  }

  if (isChanged === true) {
    const urlData: { pathname: string; search?: string; state?: { replaceUrl: boolean } } = {
      pathname: window.location.pathname,
    }

    const search = queryString.stringify(newParams)
    if (search != null && search.length > 0) {
      // url += `?${search}`
      urlData.search = `?${search}`
    }

    urlData.state = {
      replaceUrl: true,
    }

    // history.push(url)
    history.replace(urlData as any)
  }
}

export function threadTimeFormat(time: string | null | undefined): string {
  if (!time) return '-'

  const now = moment()
  const itemDate = moment(timeFormat(time, 'YYYY-MM-DDTHH:mm:ss'))

  if (!itemDate.isValid()) return '-'

  // Get settings
  const timeFormatStr = settings.getValue('general').timeFormat || 'HH:mm'
  const dateFormatStr = settings.getValue('general').dateFormat || 'YYYY-MM-DD'
  const calendarSettings = settings.getValue('calendar') || {}
  const firstDayOfWeek = calendarSettings.firstDayOfWeek || moment.localeData().firstDayOfWeek()

  // Define date ranges
  const todayStart = now.clone().startOf('day')
  const todayEnd = now.clone().endOf('day')
  const yesterdayStart = now.clone().subtract(1, 'day').startOf('day')
  const yesterdayEnd = now.clone().subtract(1, 'day').endOf('day')

  // This week calculation with custom first day of week
  const thisWeekStart = now
    .clone()
    .startOf('isoWeek')
    .add(firstDayOfWeek === 0 ? -1 : 0, 'days') // Adjust for Sunday start
  const dayBeforeYesterday = now.clone().subtract(2, 'days').endOf('day')

  // Last week
  const lastWeekStart = thisWeekStart.clone().subtract(1, 'week')
  const lastWeekEnd = thisWeekStart.clone().subtract(1, 'day').endOf('day')

  // This month
  const thisMonthStart = now.clone().startOf('month')
  const thisMonthRangeEnd = Math.min(dayBeforeYesterday.valueOf(), lastWeekEnd.valueOf())

  // Last month
  const lastMonthStart = now.clone().subtract(1, 'month').startOf('month')
  const lastMonthEnd = now.clone().subtract(1, 'month').endOf('month')

  // This year
  const thisYearStart = now.clone().startOf('year')

  const itemTime = itemDate.valueOf()
  const daysDiff = now.diff(itemDate, 'days')

  // Today
  if (itemDate.isBetween(todayStart, todayEnd, null, '[]')) {
    return itemDate.format(timeFormatStr)
  }

  // Yesterday
  if (itemDate.isBetween(yesterdayStart, yesterdayEnd, null, '[]')) {
    return itemDate.format(`ddd ${timeFormatStr}`)
  }

  // This week (The first day of the week ~ The day before yesterday)
  if (itemTime >= thisWeekStart.valueOf() && itemTime <= dayBeforeYesterday.valueOf()) {
    if (daysDiff <= 7) {
      // 최근 7일 내: Wed 20:22
      return itemDate.format(`ddd ${timeFormatStr}`)
    } else {
      // 최근 7일 후: Fri 11-08
      return itemDate.format('ddd MM-DD')
    }
  }

  // Last week
  if (itemTime >= lastWeekStart.valueOf() && itemTime <= lastWeekEnd.valueOf()) {
    return itemDate.format('ddd MM-DD')
  }

  // This month (only if there's a valid range after excluding this week)
  if (
    thisMonthRangeEnd >= thisMonthStart.valueOf() &&
    itemTime >= thisMonthStart.valueOf() &&
    itemTime <= thisMonthRangeEnd
  ) {
    if (daysDiff <= 30) {
      // 최근 30일 내: Mon 10-16
      return itemDate.format('ddd MM-DD')
    } else {
      // 최근 30일 후: 2024-10-15
      return itemDate.format(dateFormatStr)
    }
  }

  // Last month
  if (itemTime >= lastMonthStart.valueOf() && itemTime <= lastMonthEnd.valueOf()) {
    return itemDate.format(dateFormatStr)
  }

  // This year (but before last month)
  if (itemTime >= thisYearStart.valueOf() && itemTime < lastMonthStart.valueOf()) {
    return itemDate.format(dateFormatStr)
  }

  // Previous years
  return itemDate.format(dateFormatStr)
}

interface UiInfo {
  domain?: string
}

const uiInfo: UiInfo = {}

export function getUserDomain(): string {
  if (uiInfo.domain == null) {
    const { user } = reduxStore.getState()
    const parsed = emailAddress(user.email)
    if (parsed == null) {
      return ''
    }
    uiInfo.domain = `@${parsed.host}`
  }

  return uiInfo.domain
}

interface DisplayEmailOptions {
  name?: string | null
  email: string
}

export function getDisplayEmailStr(
  options: DisplayEmailOptions,
  forceAllView: boolean = false,
): string {
  const { name, email } = options
  if (isEmail(email) === true) {
    const domain = getUserDomain()

    if (forceAllView !== true && domain !== '' && email.endsWith(domain) === true) {
      return name || email
    }

    if (name == null || name === email) {
      return email
    }

    return `${name} <${email}>`
  }

  return email
}

export interface PaginationInfoOptions {
  name: string
  offset: number
  limit: number
  searchItems?: any
  useSort?: boolean
  onPaginationChange?: (page: number, limit: number, self: PaginationInfoInstance) => void
  paramNames?: {
    sort?: string
    order?: string
  }
  active?: any
  subtype?: any
  dateStart?: any
  dateEnd?: any
  showLessItems?: boolean
}

export interface PaginationInfoInstance {
  name: string
  offset: number
  limit: number
  total: number
  active: any
  subtype: any
  dateStart: any
  dateEnd: any
  showLessItems?: boolean
  paramNames: {
    valSort: string
    valOrder: string
  }
  extParams: Record<string, any>
  searchItems: any
  searchFilters: Record<string, any>
  sorts: Record<string, any>
  useSort: boolean
  searchFormObj?: any
  nsort?: any
  mount: () => void
  checkParams: () => void
  setExtParams: (params: Record<string, any>) => void
  getReqParam: () => any
  applyRes: (resPagination: any) => void
  getPagination: () => any
  getOnChange: () => any
  getColumns: (columns: any[]) => any[]
  setPage: (page: number | string, skipRefetch?: boolean, useSearchParam?: boolean) => void
  getSearchItems: () => any
  getSearchFilters: () => Record<string, any>
  resetSearch: () => void
  checkPageReplace: (props: any, prevProps: any) => void
  search: (
    key: string,
    value: string | null,
    preset?: any,
    skipRefetch?: boolean,
    useSearchParam?: boolean,
  ) => boolean
  setSort: (sortField: string | null, sortOrder?: string) => void
  getCurrentSort: () => { field: string | null; order: string | null }
}

function PaginationInfo(this: PaginationInfoInstance, options: PaginationInfoOptions) {
  const {
    name,
    offset,
    limit,
    searchItems,
    useSort,
    // defaultSort,
    onPaginationChange,
    paramNames,
    active,
    subtype,
    dateStart,
    dateEnd,
    showLessItems,
  } = options
  const self = this

  self.name = name
  self.offset = offset
  self.limit = limit
  self.total = 0
  self.active = active
  self.subtype = subtype
  self.dateStart = dateStart
  self.dateEnd = dateEnd
  self.showLessItems = showLessItems

  self.paramNames = {
    valSort: paramNames != null && paramNames.sort != null ? paramNames.sort : 'sort',
    valOrder: paramNames != null && paramNames.order != null ? paramNames.order : 'order',
  }

  const { valSort, valOrder } = self.paramNames

  self.extParams = {}

  self.searchItems = searchItems
  self.searchFilters = {}
  self.sorts = {}

  self.useSort = useSort === true

  self.mount = () => {
    const params = getSearchParamProps()
    console.log(' --- params --- ', params)

    let num = 1

    if (params != null) {
      const { page, nsearch, nsort, sort, order } = params

      if (page != null) {
        num = typeof page === 'number' ? page : Array.isArray(page) ? Number(page[0]) : Number(page)
      }

      let extParams: Record<string, any> = {}
      if (nsearch != null) {
        self.searchFilters = {}
        Object.entries(nsearch).forEach(([key, value]) => {
          self.searchFilters[key] = value
        })

        extParams = {
          ...extParams,
          ...self.searchFilters,
        }
      }

      if (sort != null || order != null) {
        self.sorts = {}
        self.sorts[valSort] = sort
        self.sorts[valOrder] = order

        extParams = {
          ...extParams,
          ...self.sorts,
        }
      }

      // URL에서 정렬 정보 복원
      if (self.useSort === true && nsort != null) {
        self.sorts = {}
        Object.entries(nsort).forEach(([key, value]) => {
          self.sorts[valSort] = key
          self.sorts[valOrder] = value === true ? 'asc' : 'desc'
        })

        extParams = {
          ...extParams,
          ...self.sorts,
        }
      }

      this.setExtParams(extParams)

      if (self.searchFormObj != null) {
        self.searchFormObj.checkParams()
      }
    }

    self.setPage(num, false, false)
  }

  self.checkParams = () => {
    const params = getSearchParamProps()

    let num = 1

    if (params != null) {
      const { page, nsearch, nsort } = params
      if (page != null) {
        num = typeof page === 'number' ? page : Array.isArray(page) ? Number(page[0]) : Number(page)
      }

      let extParams: Record<string, any> = {}
      if (nsearch != null) {
        self.searchFilters = {}
        Object.entries(nsearch).forEach(([key, value]) => {
          self.searchFilters[key] = value
        })

        extParams = {
          ...extParams,
          ...self.searchFilters,
        }
      }

      if (self.useSort === true && nsort != null) {
        self.sorts = {}
        Object.entries(nsort).forEach(([key, value]) => {
          self.sorts[valSort] = key
          self.sorts[valOrder] = value === true ? 'asc' : 'desc'
        })

        extParams = {
          ...extParams,
          // ...self.sorts,
        }
      }

      this.setExtParams(extParams)
    }

    self.setPage(num, false, false)
  }

  self.setExtParams = (params: Record<string, any>) => {
    self.extParams = params
  }

  self.getReqParam = () => {
    return {
      offset: self.offset,
      limit: self.limit,
      dateStart: self.dateStart,
      dateEnd: self.dateEnd,
      ...self.extParams,
    }
  }

  self.applyRes = (resPagination: any) => {
    self.offset = resPagination.offset
    self.limit = resPagination.limit
    self.total = resPagination.total
    ;(self as any).option = resPagination.option
    self.active = resPagination.active
    self.subtype = resPagination.subtype
    self.dateStart = resPagination.dateStart
    self.dateEnd = resPagination.dateEnd
    self.searchItems = resPagination.searchItems
  }

  self.getPagination = () => {
    const current = Math.floor(self.offset / self.limit) + 1
    const ret: any = {
      current,
      pageSize: self.limit,
      total: self.total,
      active: self.active,
      subtype: self.subtype,
      dateStart: self.dateStart,
      dateEnd: self.dateEnd,
      showLessItems: self.showLessItems,
      nsort: self.nsort,
    }

    if (self.useSort !== true) {
      ret.onChange = (page: number, pageSize: number) => {
        self.limit = pageSize
        self.setPage(page, true, true)
      }
    }

    return ret
  }

  self.getOnChange = () => {
    return async (pagination: any, filters: any, sorter: any, extra: any) => {
      // console.log(' --- filters: ', filters)
      if (self.useSort !== true) {
        return
      }

      let curPage = 1
      if (pagination != null) {
        const { current, pageSize } = pagination
        curPage = current != null ? current : 1
        self.limit = pageSize
      }

      if (extra.action === 'sort') {
        // const { field, order } = sorter
        // console.log('sorter - ', sorter)
        // const sort = {}
        // const sortMap = {}
        // if (order === 'ascend' || order === 'descend') {
        //   ;[sort[valSort]] = field
        //   sort[valOrder] = order === 'ascend' ? 'asc' : 'desc'
        //   sortMap[sort[valOrder]] = order === 'ascend'
        // } else {
        //   delete self.extParams[valSort]
        //   delete self.extParams[valOrder]
        // }
        // const param = {}
        // setSearchParams(param)
        // this.setExtParams({
        //   ...self.extParams,
        // })
      } else {
        self.setPage(curPage, false, true)
      }
    }
  }

  self.getColumns = (columns: any[]) => {
    if (self.useSort !== true) {
      return columns
    }

    const newColumns: any[] = []
    columns.forEach((col) => {
      const { dataIndex } = col

      const [sort] = dataIndex
      let sortOrder = null
      if (self.extParams[valSort] === sort) {
        sortOrder = self.extParams[valOrder] === 'desc' ? 'descend' : 'ascend'
      }

      const newCol = {
        ...col,
      }

      if (col.sorter !== false) {
        newCol.sorter = true
        newCol.sortOrder = sortOrder
      }

      newColumns.push(newCol)
    })

    return newColumns
  }

  self.setPage = (page: number | string, skipRefetch?: boolean, useSearchParam?: boolean) => {
    let num = page || 1
    if (typeof page === 'string') {
      try {
        num = parseInt(page, 10)
      } catch {
        num = 1
      }
    }
    self.offset = ((num as number) - 1) * self.limit

    if (onPaginationChange != null) {
      if (useSearchParam !== false) {
        const params: Record<string, any> = {}
        if ((num as number) > 1) {
          params[self.name] = num
        } else {
          params[self.name] = undefined
        }

        setSearchParams(params)
      }

      if (skipRefetch !== true) {
        onPaginationChange(num as number, self.limit, self)
      }
    }
  }

  // for searchForm
  self.getSearchItems = () => {
    return self.searchItems
  }

  // for searchForm
  self.getSearchFilters = () => {
    return self.searchFilters
  }

  self.resetSearch = (/* skipRefetch, extItem */) => {
    Object.keys(self.searchFilters).forEach((key) => {
      if (self.extParams[key] != null) {
        delete self.extParams[key]
      }
    })
    self.searchFilters = {}
  }

  self.checkPageReplace = (props: any, prevProps: any) => {
    if (props.location != null && prevProps.location != null) {
      if (checkPageReplace(props.location, prevProps.location) === false) {
        console.log(' ----  checkPageReplace ', props, prevProps)

        self.checkParams()
      }
    }
  }

  // for searchForm
  self.search = (
    key: string,
    value: string | null,
    preset?: any,
    skipRefetch?: boolean,
    useSearchParam?: boolean,
  ) => {
    console.log('search - ', key, value, preset, skipRefetch, useSearchParam)

    self.searchFilters = {}

    const params: Record<string, any> = {
      nsearch: undefined,
    }

    if (value != null) {
      self.searchFilters[key] = undefined

      if (value.trim().length > 0) {
        self.searchFilters[key] = value
        params[key] = value
        // params.nsearch = encodeURIComponent(encodeNSearch(self.searchFilters))
      }
    }

    const extParams = {
      ...self.extParams,
      ...self.searchFilters,
    }

    self.setExtParams(extParams)

    if (useSearchParam === true) {
      setSearchParams(params)
    }

    console.log('onSearch2 - ', key, value)

    self.setPage(1, skipRefetch, useSearchParam)

    return true
  }

  self.setSort = (sortField: string | null, sortOrder?: string) => {
    if (self.useSort !== true) {
      return
    }

    const params: Record<string, any> = {}

    if (sortField === null) {
      self.sorts = {}
      params[valSort] = undefined
      params[valOrder] = undefined
    } else {
      self.sorts = {}
      self.sorts[valSort] = sortField
      self.sorts[valOrder] = sortOrder || 'desc'

      params[valSort] = sortField
      params[valOrder] = sortOrder || 'desc'
    }

    const extParams = {
      ...self.extParams,
      ...self.sorts,
    }

    self.setExtParams(extParams)

    setSearchParams(params)

    self.setPage(1, false, true)
  }

  self.getCurrentSort = () => {
    return {
      field: self.sorts[valSort] || null,
      order: self.sorts[valOrder] || null,
    }
  }
}

export function createPaginationInfo(
  options: PaginationInfoOptions | number,
  limit?: number,
  onPaginationChange?: (page: number, limit: number, self: PaginationInfoInstance) => void,
): PaginationInfoInstance {
  // Support both object and individual parameters for backwards compatibility
  if (typeof options === 'object') {
    return new (PaginationInfo as any)(options)
  }
  return new (PaginationInfo as any)({
    name: 'page',
    offset: options,
    limit: limit!,
    onPaginationChange,
  })
}
